/**
 *  @fileOverview Analytics module
 *
 *  @author       Daniel Jung <daniel.jung@possible.com>
 *
 *  @requires     NPM:lodash
 */

import liveBinding from '../utils/live-event-binding';
import { requestTimeout } from '../utils/request-timeout';

const LOCAL_TEST = false;
const ENV = window.location.hostname.match(/localhost|10\.|127\./ig) ? 'dev' : 'prod';

export const getUriForEvents = () => {
  let eventUri = window.location.pathname;
  if (eventUri === '/') {
    eventUri = '/home/';
  }
  return eventUri;
};

export const trackEvent = (obj) => {
  const eventObject = {
    event_category: (obj && obj.eventCategory) || '',
    event_action: (obj && obj.eventAction) || '',
    event_content: (obj && obj.eventContent) || '',
    page_name: (obj && obj.pageName) || '',
    page_url: getUriForEvents(),
    event_other: (obj && obj.eventOther) || '',
  };
  window.utag.link(eventObject);
};

class Analytics {
  constructor() {
    const account =
      document.body.getAttribute('data-account') || 'hsbc';
    const profile =
      document.body.getAttribute('data-profile') || 'uk-staff-future-focus';

    this.utagSyncURI
      = `//tags.tiqcdn.com/utag/${account}/${profile}/${ENV}/utag.sync.js`;

    this.utagURI
      = `//tags.tiqcdn.com/utag/${account}/${profile}/${ENV}/utag.js`;

    let pageUrl = document.body.getAttribute('data-page-url').replace(`${window.location.protocol}//${window.location.host}`, '');
    pageUrl = pageUrl.replace(':443', '');

    if (pageUrl === '/') {
      pageUrl = '/wizard';
    }

    this.rawUtagData = {
      page_channel: 'web',
      site_domain: 'pensions',
      site_region: 'Europe',
      site_subregion: 'UK',
      site_country: 'United Kingdom',
      site_entity: 'pensions',
      site_brand: 'pensions',
      page_language: 'en_GB',
      page_security_level: 30,
      page_business_line: 'hsbc internal',
      page_customer_group: 'hsbc internal',
      site_type: 'hsbc internal',
      ib_type: 'none-ib',
      page_name: document.body.getAttribute('data-page-name'),
      page_url: pageUrl,
      page_type: document.body.getAttribute('data-page-type'),
    };
  }

  init() {
    requestTimeout(() => {
      this.loadUtagSync(() => {
        this.initStaticPageData();
        this.initDynamicPageData();
        this.loadUtag();
      }, this.handleUtagSyncErr);
    }, 1000);
  }

  loadUtag(a, b, c, d) {
    /* eslint-disable no-param-reassign, prefer-destructuring */
    a = this.utagURI;
    b = document;
    c = 'script';
    d = b.createElement(c);
    d.src = a;
    d.type = `text/java${c}`;
    d.async = true;
    a = b.getElementsByTagName(c)[0];
    a.parentNode.insertBefore(d, a);
    /* eslint-enable */
  }

  loadUtagSync(successCallback, errorCallback) {
    const script = document.createElement('script');
    script.onload = successCallback;
    script.onerror = errorCallback;
    script.src = this.utagSyncURI;

    document.head.appendChild(script);
  }

  initStaticPageData() {
    if (LOCAL_TEST) {
      this.appendScript(this.rawUtagData);
    } else {
      window.utag_data = this.rawUtagData;
    }
  }

  appendScript(data) {
    const script = document.createElement('script');
    script.innerHTML = `var utag_data = ${JSON.stringify(data).replace(/,/g, ',\n')}`;
    document.head.appendChild(script);
  }

  initDynamicPageData() {
    liveBinding('[data-event-category]', 'click', (e, el) => {
      if (el.getAttribute('data-event-category') === 'share') {
        trackEvent({
          eventCategory: el.getAttribute('data-event-category'),
          eventAction: el.getAttribute('data-event-action'),
          eventContent: el.getAttribute('data-event-content'),
          pageName: `futurefocus:${this.getUrlForTealium()}`,
        });
      } else {
        trackEvent({
          eventCategory: el.getAttribute('data-event-category'),
          eventAction: el.getAttribute('data-event-action'),
          eventContent: el.getAttribute('data-event-content'),
        });
      }
    });
  }

  getUrlForTealium() {
    const urlWithoutParams = window.location.toString().split('?')[0];
    const urlChunks = urlWithoutParams.split('/');
    return urlChunks[urlChunks.length - 1];
  }

  handleUtagSyncErr() {
    /* eslint-disable no-console */
    console.error(`Could not load: ${this.utagSyncURI}`);
    /* eslint-enable */
  }
}

export const analytics = new Analytics();



// WEBPACK FOOTER //
// ./src/js/modules/analytics.js